'use client'

import { Skeleton } from '@/components/ui/skeleton'
import { getEventResultsPublicationStatus } from '@/helpers/event-results'
import { CircleArrowLeft } from 'lucide-react'
import Link from 'next/link'
import { useMemo, useState } from 'react'
import SelectChampionshipSplitsButton from '../../components/select-championship-splits-button'
import StandingsSection from '../../components/standings/standings-section'
import ChampEventDriversSection from './champ-event-drivers-section'
import EventCarousel from './event-carousel'
import EventResultsPublicationNotice from './event-results-publication-notice'
import IracingLeaderboard from './iracing/iracing-leaderboard'
import SessionCarousel from './session-carousel'

const EMPTY_ARRAY = Object.freeze([])

export default function ChampionshipEventContent({
  event,
  isFinished,
  selectedSplits,
  setSelectedSplits,
  params,
  session,
  eventDrivers,
  eventDriversLoading,
  mutateEventDrivers,
}) {
  const eventSessions = Array.isArray(event?.sessions)
    ? event.sessions
    : EMPTY_ARRAY
  const championshipSplits = Array.isArray(event?.championship?.splits)
    ? event.championship.splits
    : EMPTY_ARRAY
  const publicationStatus = getEventResultsPublicationStatus(eventSessions)
  const showIRacingLeaderboard = Boolean(
    !publicationStatus.hasPublishedResults &&
    event?.metadata?.['iracing-time-attack-url'],
  )
  const showScheduleAndDrivers = Boolean(
    !showIRacingLeaderboard &&
    (!publicationStatus.hasPublishedResults ||
      publicationStatus.isPartiallyPublished),
  )
  const standingsEvents = useMemo(() => [event], [event])

  const [now] = useState(() => new Date())

  return (
    <>
      {publicationStatus.hasPublishedResults ? (
        <div className="screen-grid *:grid-indent">
          <EventResultsPublicationNotice
            publicationStatus={publicationStatus}
          />

          <StandingsSection
            championship={event.championship}
            events={standingsEvents}
            selectedSplits={selectedSplits}
            setSelectedSplits={setSelectedSplits}
            presetEventId={event.id}
          />

          <div className="divide mt-8 hidden h-px bg-neutral-blue-700 desktop-xs:mt-[60px] desktop-xs:block" />
        </div>
      ) : showIRacingLeaderboard ? (
        <div className="screen-grid *:grid-indent mt-6">
          <IracingLeaderboard
            event={event}
            eventAttendees={eventDrivers || []}
            session={session}
            params={params}
          />
        </div>
      ) : null}

      {showScheduleAndDrivers ? (
        <ChampionshipEventSchedule
          event={event}
          isFinished={isFinished}
          championshipSplits={championshipSplits}
          selectedSplits={selectedSplits}
          setSelectedSplits={setSelectedSplits}
          params={params}
          session={session}
          eventDrivers={eventDrivers}
          eventDriversLoading={eventDriversLoading}
          mutateEventDrivers={mutateEventDrivers}
        />
      ) : null}

      <div className="screen-grid *:grid-indent">
        <h4 className="mt-8 text-h-5 font-semibold text-shades-0 desktop-xs:mt-[60px] desktop-xs:text-h-4">
          More upcoming events in this Championship
        </h4>
      </div>

      <EventCarousel
        params={params}
        currentEventId={event.id}
        where={{
          startDateTime: {
            gte: now,
          },
        }}
      />

      <div className="screen-grid">
        <Link
          href={`/c/${params.communitySlug}/${params.championshipSlug}?tabName=Event-Schedule`}
          className="group col-span-full mx-2 mt-[80px] flex h-auto items-center gap-3 rounded-sm align-middle desktop-xs:mt-[100px] desktop-xs:pb-[20px]"
        >
          <CircleArrowLeft className="ml-auto mt-[2px] h-5 w-5 stroke-[1.5px]" />
          <p className="mr-auto w-max text-p-1 font-semibold group-hover:underline group-hover:underline-offset-2">
            Back to Championship page
          </p>
        </Link>
      </div>
    </>
  )
}

function ChampionshipEventSchedule({
  event,
  isFinished,
  championshipSplits,
  selectedSplits,
  setSelectedSplits,
  params,
  session,
  eventDrivers,
  eventDriversLoading,
  mutateEventDrivers,
}) {
  return (
    <>
      <div className="mt-6 desktop-xs:mt-[52px]" />

      {championshipSplits.length > 0 ? (
        <div className="screen-grid *:grid-indent mb-6">
          <div className="flex items-start">
            <SelectChampionshipSplitsButton
              selectedSplits={selectedSplits}
              setSelectedSplits={setSelectedSplits}
              splits={championshipSplits}
            />
          </div>

          <div className="divide mt-5" />
        </div>
      ) : null}

      <SessionCarousel
        event={event}
        selectedSplits={selectedSplits}
        params={params}
      />

      <div className="screen-grid *:grid-indent">
        <EventDriversSection
          event={event}
          isFinished={isFinished}
          selectedSplits={selectedSplits}
          session={session}
          eventDrivers={eventDrivers}
          eventDriversLoading={eventDriversLoading}
          mutateEventDrivers={mutateEventDrivers}
        />
      </div>
    </>
  )
}

function EventDriversSection({
  event,
  isFinished,
  selectedSplits,
  session,
  eventDrivers,
  eventDriversLoading,
  mutateEventDrivers,
}) {
  const eventDriversList = Array.isArray(eventDrivers)
    ? eventDrivers
    : EMPTY_ARRAY
  const selectedSplitIds = Array.isArray(selectedSplits)
    ? selectedSplits
    : EMPTY_ARRAY
  const championshipSplits = Array.isArray(event?.championship?.splits)
    ? event.championship.splits
    : EMPTY_ARRAY
  const championshipCarClasses = Array.isArray(event?.championship?.carClasses)
    ? event.championship.carClasses
    : EMPTY_ARRAY

  const splitDrivers = useMemo(() => {
    if (eventDriversList.length === 0) return []
    if (championshipSplits.length === 0) return eventDriversList

    return eventDriversList.filter((d) => {
      const attendingChampionships = Array.isArray(
        d?.user?.attendingChampionships,
      )
        ? d.user.attendingChampionships
        : []
      const attendingChampionship = attendingChampionships.find(
        (c) => c.championshipId === event.championshipId,
      )

      if (!attendingChampionship || !attendingChampionship.splitId) return true

      return selectedSplitIds.includes(attendingChampionship.splitId)
    })
  }, [
    eventDriversList,
    selectedSplitIds,
    championshipSplits,
    event.championshipId,
  ])

  const showSplits =
    championshipSplits.length > 0 &&
    splitDrivers.some((d) =>
      (Array.isArray(d?.user?.attendingChampionships)
        ? d.user.attendingChampionships
        : []
      ).some((c) => c.splitId),
    )

  const showCarClasses =
    championshipCarClasses.length > 0 &&
    splitDrivers.some((d) =>
      (Array.isArray(d?.user?.attendingChampionships)
        ? d.user.attendingChampionships
        : []
      ).some((c) => c.carClassId),
    )

  return (
    <>
      <div className="mt-8 flex items-center gap-2 align-middle desktop-xs:mt-[60px]">
        <h5 className="text-h-5 font-semibold desktop-xs:text-h-4">
          {isFinished ? 'Drivers Attended' : 'Drivers Attending'}
        </h5>
      </div>

      <div className="divide mt-6 hidden h-px bg-neutral-blue-700 desktop-xs:block" />

      {eventDriversLoading ? (
        <Skeleton className="mt-6 h-[600px] w-full" />
      ) : (
        <ChampEventDriversSection
          event={event}
          isFinished={isFinished}
          session={session}
          eventDrivers={splitDrivers}
          mutateEventDrivers={mutateEventDrivers}
          showSplits={showSplits}
          showCarClasses={showCarClasses}
        />
      )}
    </>
  )
}
